import { type FC, useMemo } from "react";
import { Card, type CardProps, CardRowSimple, LoadingSpinner } from "widgets";
import { useVisuallyLoading } from "widgets/hooks";
import { formatDateTimeFromISO, formatNumberByThousands, humanReadableFileSize } from "widgets/utils";

import { useError } from "@/app-utils/graphql/useError.js";
import { useSubscription } from "@/app-utils/hooks/useSubscription.ts";
import { PubSubActions } from "@/global/pubsub-actions.ts";
import { IndexStatusTag } from "@/modules/files/components/IndexStatusTag.js";
import { useGetFileInfoCardQuery } from "@/modules/files/graphql/files-queries.gql.js";
import { isGenomeViewerFile } from "@/modules/files/utils/file.js";
import { getFileType } from "@/modules/files/utils/utils.js";
import { ProjectColoredLink } from "@/modules/projects/components/ProjectColoredWidgets.js";
import { FILE_BROWSE_TYPES } from "@/settings/settings.ts";
import styleCard from "@/ui-modules/core/cards/styles/Card.module.scss";
import { SmartLink } from "@/ui-modules/core/SmartLink.tsx";

interface FileCardProps extends CardProps {
  fileId: number;
}

export const FileCard: FC<FileCardProps> = (props) => {
  const { data, loading, error, refetch } = useGetFileInfoCardQuery({ variables: { fileId: props.fileId } });
  void useError(error);

  useSubscription(PubSubActions.FILE_EDIT, () => {
    void refetch();
  });

  const file = useMemo(() => data?.projectFileById, [data]);
  const { size, unit } = useMemo(() => humanReadableFileSize(file?.size ?? 0, true), [file]);

  const fakeLoading = useVisuallyLoading(loading, 300);

  return (
    <Card {...props}>
      {fakeLoading ? (
        <LoadingSpinner />
      ) : (
        file && (
          <>
            <CardRowSimple label={"Name"} value={file.fileName} />
            <CardRowSimple label={"Type"} value={getFileType(file)} />

            {isGenomeViewerFile(file) && (
              <CardRowSimple label={"Indexing status"}>
                <div className={styleCard.tags_list}>
                  <IndexStatusTag status={file.indexingStatus} />
                </div>
              </CardRowSimple>
            )}

            {file.url && (
              <CardRowSimple
                label={"URL"}
                value={
                  <SmartLink to={file.url} target={"_blank"} rel={"noreferrer"}>
                    {file.url}
                  </SmartLink>
                }
              />
            )}
            {/* Size of 0 can happen for empty (regular) files */}
            {file?.fileBrowseType === FILE_BROWSE_TYPES.REGULAR_FILE && Number(size) >= 0 && (
              <CardRowSimple label={`Size`} value={`${size} ${unit} (${formatNumberByThousands(file.size)} bytes)`} />
            )}
            {file.project && (
              <CardRowSimple
                label={"Project"}
                value={<ProjectColoredLink project={file.project} noLink />}
              ></CardRowSimple>
            )}
            <CardRowSimple label={"Path"} value={file.humanReadablePath} />
            <CardRowSimple label={"Description"} value={file.description} />
            <CardRowSimple
              label={"Added"}
              value={file.created ? formatDateTimeFromISO(file.created, { full: true }) : undefined}
            />
            {file.lastModified && (
              <CardRowSimple label={"Date modified"} value={formatDateTimeFromISO(file.lastModified, { full: true })} />
            )}
            {file.lastModifier && <CardRowSimple label={"Last modifier"} value={file.lastModifier.display} />}
            {file.creator?.display && <CardRowSimple label={"Owner"} value={file.creator.display} />}
          </>
        )
      )}
    </Card>
  );
};
